import { FeatureFlags } from '@constants/featureFlags';
import { RoutingFlags } from '@constants/routingFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { BulkOrderUploadRoute } from '@views/bulkOrderUpload/index';
import { EdiFAQRoute } from '@views/ediFAQ';
import { EquipmentSearchRoute } from '@views/equipmentSearch';
import {
  FeatureFlagExplorerRoute,
  FeatureFlagNewRoute,
  FeatureFlagRoute,
} from '@views/featureFlag';
import { FinancialAutoProcessingRoute } from '@views/financialAutoProcessing';
import {
  HelpdeskAccessRoute,
  HelpdeskEfsValidationRoute,
  HelpdeskHighwaySyncRoute,
  HelpdeskOfficeMoveRoute,
  HelpdeskResyncRoute,
  HelpdeskRevenueTriageRoute,
  HelpdeskSecureRateConRoute,
} from '@views/helpdeskOperations';
import { JiraTicketMetricsRoute } from '@views/jiraTicketMetrics';
import { MaxBuyRoute } from '@views/maxBuy';
import { documentRoutes } from '@views/pdfDocument';
import { PricingToolRoute } from '@views/pricingTool';
import { ShortPayAdminRoute } from '@views/shortPayAdmin';
import { Component, JSX } from 'solid-js';

import { AdminToolsRoute } from './views/adminTools';
import { ApCallingQueueRoute } from './views/apCallingQueue';
import { ArCallingQueueRoute } from './views/arCallingQueue';
import { BedrockAssistantRoute } from './views/bedrockAssistant';
import { BillingQueueRoute } from './views/billingQueues';
import { CapacityFinderRoute } from './views/capacityFinder';
import {
  CarrierMatchingRoute,
  // CarrierMatchingOffersRoute, -- Why is this not used?
  MyOffersRoute,
  OffersManagerRoute,
} from './views/carrierMatching';
import { CarrierRelationsQueuesRoute } from './views/carrierRelationsQueues';
import {
  CarrierDetailsRoute,
  CarrierLaneSearchRoute,
  CarrierPlaylists,
  CarrierRoute,
  NewCarrierDetailsRoute,
  SendCarrierPacketRoute,
} from './views/carriers';
import { ClaimRoute } from './views/claim';
import { ClaimsTabRoute } from './views/claims';
import { ApplicationPostingRoute } from './views/collapsableSidebar/applicationPosting';
import { CollectionsRoute } from './views/collections';
import {
  CollectionDetailsRoute,
  CollectionsDashboardRoute,
} from './views/collectionsDashboard';
import {
  CreditApplicationDetailRoute,
  CreditApplicationsRoute,
  CreditApplyRoute,
  CreditDashboardRoute,
  CreditMyApplicationsRoute,
  OfficeCreditApplicationMetricsRoute,
  CreditQueueRoute,
  CreditScoringRoute,
} from './views/creditApplications';
import {
  CustomerDetailsRoute,
  CustomersRoute,
  DormantAccountsRoute,
  NewCustomerRoute,
  NewCustomerWithProspectRoute,
} from './views/customer';
import { DocumentsPageRoute } from './views/documentsPage';
import { DormantPoolRoute } from './views/dormantPool';
import { EfsHistoryRoute } from './views/efsHistory';
import {
  FactoringCompanyDetailsCreationRoute,
  FactoringCompanyDetailsRoute,
  FactoringCompanyRoute,
} from './views/factoringCompany';
import { HoldsTabRoute } from './views/holds';
import { HomeRoute } from './views/home';
import { InvoicingDetailsRoute, InvoicingRoute } from './views/invoicing';
import { LoadboardRoute } from './views/loadboard';
import { OldLoadsRoute } from './views/loadboard/oldLoads';
import { LoadSearchRoute } from './views/loadSearch';
import { LoadTendersRoute } from './views/loadTenders';
import { PendingApprovalsTabRoute } from './views/pendingApprovals';
import { LTLRoute, SavedQuoteRoute } from './views/ltl';
import { OfficeDetailRoute, OfficeRoute } from './views/office';
import { EditHistoryRoute, OrderDetailsRoute, OrderRoute } from './views/order';
import { PostingGroupRoute } from './views/postingGroup';
import {
  NewProspectRoute,
  ProspectDetailsRoute,
  ProspectRoute,
} from './views/prospect';
import { ReleaseNotesRoute } from './views/releaseNotes';
import { RevenueDashboardRoute } from './views/revenueDashboard';
import { CarrierBlacklistReportRoute } from './views/carrierBlacklistReport';
import {
  NewRevenueSearchRoute,
  RevenueSearchRoute,
} from './views/revenueSearch';
import { RunSettlementsRoute } from './views/runSettlements';
import { SettlementDashboardRoute } from './views/settlementDashboard';
import { TrackTraceRoute } from './views/trackTrace';
import { UploadToolRoute } from './views/uploadTool';
import { UserDetailsRoute } from './views/userDetails';
import {
  NewVendorsDetailsRoute,
  VendorsDetailsRoute,
  VendorsRoute,
} from './views/vendors';

type ComponentT =
  | (() => JSX.Element)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | ((props: any) => JSX.Element)
  | (() => Element);
type PreloadComponentT = (() => JSX.Element) &
  {
    preload: () => Promise<{
      default: () => JSX.Element;
    }>;
  }[];

export type RouteDefT = {
  path: string;
  component?: ComponentT | PreloadComponentT;
  load?: () => Promise<{
    default: (() => JSX.Element) | Component;
  }>;
  protected?: boolean;
  routingFlag?: RoutingFlags;
  featureFlag?: FeatureFlags;
  hideLayout?: boolean;
  roles?: string[];
  blacklistedRoles?: string[];
};

export const isRouteFeatureEnabled = (route: RouteDefT) =>
  route.featureFlag === undefined || isFeatureFlagEnabled(route.featureFlag)();

//PLEASE READ THIS:
// WARNING: ENSURE THAT IF A PATH IS V4 ONLY, IT IS NOT IN THE V3 ROUTES
// WARNING: V3 ROUTES TAKE PRECEDENCE OVER V4 ROUTES
// PLEASE READ THE ABOVE

export const v4Routes: RouteDefT[] = [
  HomeRoute,
  BedrockAssistantRoute,
  CreditApplyRoute,
  CreditDashboardRoute,
  OfficeCreditApplicationMetricsRoute,
  CreditApplicationsRoute,
  CreditMyApplicationsRoute,
  CreditApplicationDetailRoute,
  CreditQueueRoute,
  CreditScoringRoute,
  SettlementDashboardRoute,
  RevenueDashboardRoute,
  ClaimRoute,
  UserDetailsRoute,
  ApCallingQueueRoute,
  ArCallingQueueRoute,
  AdminToolsRoute,
  PostingGroupRoute,
  CarrierRoute,
  OrderRoute,
  LoadboardRoute,
  TrackTraceRoute,
  DormantAccountsRoute,
  SavedQuoteRoute,
  OrderDetailsRoute,
  EquipmentSearchRoute,
  CarrierMatchingRoute,
  CapacityFinderRoute,
  MyOffersRoute,
  OffersManagerRoute,
  LTLRoute,
  ClaimsTabRoute,
  HoldsTabRoute,
  OfficeRoute,
  OfficeDetailRoute,
  CustomersRoute,
  OldLoadsRoute,
  EditHistoryRoute,
  CarrierLaneSearchRoute,
  CustomerDetailsRoute,
  NewCustomerRoute,
  CarrierPlaylists,
  CarrierDetailsRoute,
  DocumentsPageRoute,
  LoadSearchRoute,
  PendingApprovalsTabRoute,
  FactoringCompanyRoute,
  FactoringCompanyDetailsRoute,
  FactoringCompanyDetailsCreationRoute,
  NewProspectRoute,
  CollectionsRoute,
  CollectionsDashboardRoute,
  NewCarrierDetailsRoute,
  CollectionDetailsRoute,
  RevenueSearchRoute,
  CarrierBlacklistReportRoute,
  NewRevenueSearchRoute,
  BulkOrderUploadRoute,
  LoadTendersRoute,
  SendCarrierPacketRoute,
  ProspectRoute,
  ProspectDetailsRoute,
  PricingToolRoute,
  NewCustomerWithProspectRoute,
  ApplicationPostingRoute,
  MaxBuyRoute,
  InvoicingRoute,
  InvoicingDetailsRoute,
  EfsHistoryRoute,
  DormantPoolRoute,
  UploadToolRoute,
  BillingQueueRoute,
  FinancialAutoProcessingRoute,
  RunSettlementsRoute,
  ReleaseNotesRoute,
  EdiFAQRoute,
  CarrierRelationsQueuesRoute,
  VendorsRoute,
  VendorsDetailsRoute,
  FeatureFlagRoute,
  FeatureFlagNewRoute,
  FeatureFlagExplorerRoute,
  NewVendorsDetailsRoute,
  HelpdeskAccessRoute,
  HelpdeskResyncRoute,
  HelpdeskOfficeMoveRoute,
  HelpdeskEfsValidationRoute,
  HelpdeskHighwaySyncRoute,
  HelpdeskRevenueTriageRoute,
  HelpdeskSecureRateConRoute,
  JiraTicketMetricsRoute,
  ShortPayAdminRoute,
  ...documentRoutes,
];

export type V3RouteDef = {
  routingFlag?: RoutingFlags;
  protected: boolean;
  path: string;
  overridePath?: string;
  subRoutes?: V3RouteDef[];
  roles?: string[];
  defaultV3?: boolean;
};

export const v3Routes: V3RouteDef[] = [
  {
    path: '/Dashboard',
    protected: true,
    routingFlag: RoutingFlags.V4RevenueDashboard,
  },
  {
    path: '/Account/forgotpassword',
    protected: false,
  },
  {
    path: '/Dashboard/Settlements',
    protected: true,
    routingFlag: RoutingFlags.V4SettlementsDashboard,
  },
  {
    path: '/Order/Search',
    protected: true,
    routingFlag: RoutingFlags.V4RevenueSearch,
  },
  {
    path: '/Load',
    protected: true,
    routingFlag: RoutingFlags.V4LoadBoard,
    subRoutes: [
      {
        path: 'Search',
        protected: true,
        routingFlag: RoutingFlags.V4LoadSearch,
      },
      {
        path: 'Tenders',
        protected: true,
        routingFlag: RoutingFlags.V4LoadTenders,
      },
      { path: 'Index', protected: true },
      {
        path: 'Claims',
        protected: true,
        routingFlag: RoutingFlags.V4ClaimsTab,
      },
      { path: 'Holds', protected: true, routingFlag: RoutingFlags.V4HoldsTab },
      { path: 'Old', protected: true, routingFlag: RoutingFlags.V4OldLoads },
    ],
  },
  {
    path: '/Order/Details',
    protected: true,
    routingFlag: RoutingFlags.V4OrderDetails,
    subRoutes: [
      {
        path: ':id',
        routingFlag: RoutingFlags.V4OrderDetails,
        protected: true,
      },
    ],
  },
  {
    path: '/Carrier',
    protected: true,
    routingFlag: RoutingFlags.V4CarrierSearch,
    subRoutes: [
      {
        path: 'Details/:id',
        routingFlag: RoutingFlags.V4CarrierDetails,
        protected: true,
      },
      {
        path: 'Playlists',
        protected: true,
        routingFlag: RoutingFlags.V4CarrierPlaylist,
      },
      {
        path: 'LaneSearch',
        protected: true,
        routingFlag: RoutingFlags.V4LaneSearch,
      },
      { path: 'BulkLaneSearch', protected: true },
      {
        path: 'EquipmentSearch',
        protected: true,
        routingFlag: RoutingFlags.V4OrderDetails,
      },
    ],
  },
  {
    path: '/FactoringCompany',
    protected: true,
    routingFlag: RoutingFlags.V4FactoringCompanyList,
    subRoutes: [
      {
        path: 'Details',
        protected: true,
        routingFlag: RoutingFlags.V4FactoringCompanyList,
      },
    ],
  },
  {
    path: '/Customer',
    protected: true,
    routingFlag: RoutingFlags.V4Customers,
    subRoutes: [
      { path: 'DormantAccounts', protected: true },
      { path: 'EDIQueue', protected: true },
      {
        path: 'Dormant',
        protected: true,
        routingFlag: RoutingFlags.V4DormantPool,
      },
      {
        path: 'Create',
        protected: true,
        routingFlag: RoutingFlags.V4CustomerCreate,
      },
    ],
  },
  {
    path: '/Prospect',
    routingFlag: RoutingFlags.V4Prospects,
    protected: true,
    subRoutes: [
      {
        path: 'Create',
        protected: true,
        routingFlag: RoutingFlags.V4NewProspect,
      },
      {
        path: 'Details',
        protected: true,
        routingFlag: RoutingFlags.V4ProspectDetails,
      },
    ],
  },
  {
    path: '/Accounting',
    protected: true,
    subRoutes: [
      {
        path: 'AdminTools',
        routingFlag: RoutingFlags.V4AdminTools,
        protected: true,
      },
      {
        path: 'Invoicing',
        routingFlag: RoutingFlags.V4Invoicing,
        protected: true,
        defaultV3: true,
      },
      {
        path: 'BillingQueue',
        protected: true,
        routingFlag: RoutingFlags.V4BillingQueues,
        defaultV3: true,
      },
      {
        path: 'APCallingQueue',
        protected: true,
        routingFlag: RoutingFlags.V4APCallingQueue,
      },
      { path: 'Groups', protected: true },
      {
        path: 'EFSHistory',
        routingFlag: RoutingFlags.V4EfsHistory,
        protected: true,
      },
      {
        path: 'Collections',
        protected: true,
        routingFlag: RoutingFlags.V4Collections,
      },
      { path: 'CreditInsurance', protected: true },
      { path: 'ARCallingQueue', protected: true },
      {
        path: 'Settlements',
        protected: true,
        routingFlag: RoutingFlags.V4Settlements,
      },
      {
        path: 'UploadTool',
        routingFlag: RoutingFlags.V4UploadTool,
        protected: true,
      },
    ],
  },
  {
    path: '/User/Details',
    protected: true,
    routingFlag: RoutingFlags.V4UserDetails,
  },
  {
    path: '/Document',
    protected: true,
    routingFlag: RoutingFlags.V4AdminDocuments,
  },
  {
    path: '/Office',
    protected: true,
    routingFlag: RoutingFlags.V4OfficeList,
    subRoutes: [
      {
        path: 'Details',
        protected: true,
        routingFlag: RoutingFlags.V4OfficeDetails,
      },
    ],
  },
  {
    path: '/application/orgchart',
    protected: true,
  },
  {
    path: '/application/releaseNotes',
    protected: true,
    routingFlag: RoutingFlags.V4ReleaseNotes,
  },
  {
    path: '/EDI/FAQ',
    protected: true,
    routingFlag: RoutingFlags.V4EdiFaq,
  },
  {
    path: '/Maxbuy',
    protected: true,
    routingFlag: RoutingFlags.MaxBuy,
  },
  {
    path: '/customer/dormant',
    protected: true,
    routingFlag: RoutingFlags.V4DormantPool,
  },
  {
    path: '/document/invoice/:id',
    protected: true,
    routingFlag: RoutingFlags.V4BolRateConInvoice,
  },
  {
    path: '/document/BillOfLading/:id',
    protected: true,
    routingFlag: RoutingFlags.V4BolRateConInvoice,
  },
  {
    path: '/document/CarrierRateConfirmation/:id',
    protected: true,
    routingFlag: RoutingFlags.V4BolRateConInvoice,
  },
];

//if there are V4 routes that do not correspond directly to a v3 route but need to be redirected to a v3 route, add them here
export const v4RoutesV3Routes: V3RouteDef[] = [
  {
    path: '/claimstab',
    protected: true,
    routingFlag: RoutingFlags.V4ClaimsTab,
    overridePath: '/load/Claims',
  },
  {
    path: '/loadboard/old',
    protected: true,
    routingFlag: RoutingFlags.V4OldLoads,
    overridePath: '/load/Old',
  },
  {
    path: '/loadboard',
    protected: true,
    routingFlag: RoutingFlags.V4LoadBoard,
    overridePath: '/load/index',
  },
];

export const v4RoleFilterRoutes = v4Routes.filter((r) => !!r.blacklistedRoles);
